/*
 * WhatsApp signup — the tap record, the bot checks, and the offline retry.
 *
 * The signup itself does not happen here. It happens when the shopper presses send
 * in WhatsApp, and their number reaches the ERP on that message. Everything in this
 * file is the soft record around it: which page they were on, and the exact consent
 * wording they were shown.
 *
 * Because of that, nothing here is allowed to get in the shopper's way. The POST is
 * fire-and-forget, its response is ignored on purpose (the ERP always answers 202),
 * and a failure is queued for the next page view rather than surfaced.
 *
 * Wiring: any form marked [data-wa-form] containing a [data-wa-join] link. The
 * markup lives in snippets/whatsapp-join-form.liquid; the link in
 * snippets/whatsapp-join.liquid.
 */
(function () {
  'use strict';

  var CFG = window.GRACEWA_CONFIG || {};
  var QUEUE_KEY = 'grace_wa_tap_queue';
  var SEEN_KEY = 'grace_wa_signup_seen';
  var MAX_QUEUE = 20;
  var MIN_MS = (CFG.minSeconds || 2) * 1000;

  /* ── storage helpers, all failure-tolerant ─────────────────────────────
     Private browsing and "block site data" both make localStorage throw on
     access rather than return null, so every touch is wrapped. A shopper
     with storage disabled still gets a working button; they just lose the
     retry and the once-only rule. */
  function readQueue() {
    try { return JSON.parse(window.localStorage.getItem(QUEUE_KEY) || '[]'); }
    catch (e) { return []; }
  }
  function writeQueue(q) {
    try { window.localStorage.setItem(QUEUE_KEY, JSON.stringify(q.slice(-MAX_QUEUE))); }
    catch (e) {}
  }
  function enqueue(payload) {
    var q = readQueue();
    q.push(payload);
    writeQueue(q);
  }
  function emirate() {
    try { return window.localStorage.getItem('emirates') || ''; }
    catch (e) { return ''; }
  }
  function markSeen() {
    try { window.localStorage.setItem(SEEN_KEY, '1'); } catch (e) {}
  }

  /* ── the POST ───────────────────────────────────────────────────────────
     keepalive is the point: on a phone, tapping the link hands the browser
     over to WhatsApp, and without it the request is cancelled in flight.
     The ERP answers 202 to everything by design, so a non-ok status means
     the request genuinely did not land and is worth retrying. */
  function post(payload, useKeepalive) {
    if (!CFG.endpoint) return Promise.reject(new Error('no endpoint'));
    return fetch(CFG.endpoint, {
      method: 'POST',
      mode: 'cors',
      credentials: 'omit',
      cache: 'no-store',
      keepalive: !!useKeepalive,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (r) {
      if (!r.ok) throw new Error('status ' + r.status);
      return true;
    });
  }

  function trim(value, max) {
    if (value === null || value === undefined) return undefined;
    var s = String(value).trim();
    if (!s) return undefined;
    return s.length > max ? s.slice(0, max) : s;
  }

  /* Field lengths mirror the ERP's own caps so we truncate rather than let
     them reject or silently store a clipped value. */
  function buildPayload(o) {
    var p = {
      brand: CFG.brand || 'black-and-blanc',
      consent: true,
      consent_text: trim(o.consentText, 500)
    };
    var optional = {
      source: trim(o.source, 40),
      name: trim(o.name, 80),
      page: trim(o.page || window.location.href, 300),
      emirate: trim(o.emirate || emirate(), 60),
      market: trim(CFG.market, 8),
      locale: trim(CFG.locale, 12),
      shopify_customer_id: trim(CFG.customerId, 40)
    };
    for (var k in optional) {
      if (optional[k] !== undefined) p[k] = optional[k];
    }
    return p;
  }

  function tap(o) {
    var payload = buildPayload(o);
    if (!payload.consent_text) return;          // no wording, no record worth keeping
    post(payload, true).catch(function () { enqueue(payload); });
  }

  /* ── retry, once per page view ─────────────────────────────────────────
     Taken off the queue before sending and put back only on failure, so a
     tab that closes mid-retry loses nothing. Duplicates are safe at the ERP:
     a repeated tap is two rows in a soft log, never two customers. */
  function drain() {
    if (navigator.onLine === false) return;
    var q = readQueue();
    if (!q.length) return;
    writeQueue([]);
    q.forEach(function (payload) {
      post(payload, false).catch(function () { enqueue(payload); });
    });
  }

  /* ── form wiring ───────────────────────────────────────────────────────── */
  function wire(form) {
    if (form.getAttribute('data-wa-wired') === '1') return;
    form.setAttribute('data-wa-wired', '1');

    var link = form.querySelector('[data-wa-join]');
    if (!link) return;

    var consentText = form.querySelector('[data-wa-consent-text]');
    var honeypot = form.querySelector('[data-wa-hp]');
    var nameField = form.querySelector('[data-wa-name]');
    var errorEl = form.querySelector('[data-wa-error]');
    var doneEl = form.querySelector('[data-wa-done]');
    var bodyEl = form.querySelector('[data-wa-body]');

    /* The clock starts when the form is actually on screen, not when the page
       parsed. It matters for the popup, which appears six seconds after the
       emirate selector is dismissed — timed from page load, the two-second
       minimum would already have elapsed before anyone could see the thing,
       and the bot check would be measuring nothing. */
    var shownAt = Date.now();
    if (typeof IntersectionObserver === 'function') {
      var seen = false;
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!seen && entry.isIntersecting) {
            seen = true;
            shownAt = Date.now();
            io.disconnect();
          }
        });
      }, { threshold: 0.4 });
      io.observe(form);
    }

    function setError(message) {
      if (!errorEl) return;
      errorEl.textContent = message || '';
      errorEl.hidden = !message;
    }

    /* Shown to bots as well as people. A bot told it failed tries something
       else; a bot told "thank you" goes away believing it worked. */
    function showDone() {
      if (bodyEl) bodyEl.hidden = true;
      if (doneEl) doneEl.hidden = false;
      markSeen();
      form.dispatchEvent(new CustomEvent('grace:wa-joined', { bubbles: true }));
    }

    /* Consent is the press itself, not a tick box — see the note in
       snippets/whatsapp-join-form.liquid. The gate is the name field instead, so
       the press is always deliberate rather than a stray tap on a live button.
       Where there is no name field at all (the footer) the button is open from
       the start, and the press remains the affirmative act. */
    function ready() {
      return !nameField || nameField.value.trim().length > 0;
    }

    function syncEnabled() {
      var ok = ready();
      link.classList.toggle('is-disabled', !ok);
      link.setAttribute('aria-disabled', ok ? 'false' : 'true');
      if (ok) setError('');
    }

    if (nameField) {
      nameField.addEventListener('input', syncEnabled);
      nameField.addEventListener('change', syncEnabled);
    }
    syncEnabled();

    link.addEventListener('click', function (ev) {
      // Trap first: no WhatsApp, no record, but the shopper-facing success state.
      if (honeypot && honeypot.value) { ev.preventDefault(); showDone(); return; }
      if (Date.now() - shownAt < MIN_MS) { ev.preventDefault(); showDone(); return; }

      if (!ready()) {
        ev.preventDefault();
        setError(form.getAttribute('data-wa-error-text') || 'Please add your first name.');
        nameField.focus();
        return;
      }

      tap({
        source: link.getAttribute('data-wa-source'),
        name: nameField ? nameField.value : '',
        consentText: consentText ? consentText.textContent : ''
      });

      showDone();                               // the link itself opens WhatsApp
    });
  }

  function wireAll(root) {
    (root || document).querySelectorAll('[data-wa-form]').forEach(wire);
  }

  function init() {
    wireAll(document);
    drain();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Sections re-render in the theme editor; re-wire rather than go inert.
  document.addEventListener('shopify:section:load', function (e) { wireAll(e.target); });

  window.GRACEWA = { tap: tap, wire: wire, wireAll: wireAll, drain: drain, seenKey: SEEN_KEY };
})();
